import { useContext } from 'react';
import { PlatformContext } from '@/providers/PlatformProvider';

/**
 * The Glanz Luxe wordmark — BUILD-PLAN §3/§4.
 *
 * Faithful to the supplied artwork, in which:
 *   • "Glanz" is a GOLD handwritten script
 *   • "LUXE" is BLACK, wide-spaced uppercase, beneath it
 *   • a thin gold rule with a solid gold heart sits BELOW "LUXE",
 *     broken either side of the heart
 *
 * §3 forbids distorting the logo, so proportions are fixed and only overall
 * scale varies.
 *
 * If real artwork is configured (brand.assets.logo_full), it is used in
 * preference to this type-set reconstruction — the script face here is a
 * stand-in, not the true lettering. Drop a file at
 * `frontend/public/brand/logo-full.svg` (or set BRAND_LOGO_FULL) and every
 * instance of the mark across the site switches to it, with no component
 * change (§75 brand asset management).
 */

type LogoSize = 'sm' | 'md' | 'lg' | 'xl';
type LogoTone = 'dark' | 'light';

const SIZES: Record<
  LogoSize,
  { glanz: string; luxe: string; rule: string; heart: string; gap: string }
> = {
  sm: { glanz: 'text-[1.75rem]', luxe: 'text-[0.5rem] tracking-[0.55em]', rule: 'w-20', heart: 'h-2 w-2', gap: 'mt-0.5' },
  md: { glanz: 'text-5xl', luxe: 'text-[0.6875rem] tracking-[0.6em]', rule: 'w-28', heart: 'h-2.5 w-2.5', gap: 'mt-1' },
  lg: { glanz: 'text-7xl', luxe: 'text-sm tracking-[0.65em]', rule: 'w-40', heart: 'h-3 w-3', gap: 'mt-1.5' },
  xl: { glanz: 'text-9xl', luxe: 'text-lg tracking-[0.7em]', rule: 'w-56', heart: 'h-4 w-4', gap: 'mt-2' },
};

const PIXEL_WIDTHS: Record<LogoSize, number> = { sm: 120, md: 190, lg: 280, xl: 400 };

interface LogoProps {
  size?: LogoSize;
  tone?: LogoTone;
  /**
   * Whether the mark is the page's accessible name. Set false when the logo
   * sits beside a visible brand name, to avoid announcing it twice.
   */
  decorative?: boolean;
  className?: string;
}

export function Logo({ size = 'md', tone = 'dark', decorative = false, className = '' }: LogoProps) {
  /*
   * Read the context directly rather than through usePlatform(), which throws
   * when the provider is absent. The splash screen renders this component
   * *while* platform config is still loading, so the mark must degrade to its
   * type-set form rather than crash the first paint.
   */
  const platform = useContext(PlatformContext);
  const assets = platform?.config.brand.assets;

  const asset = tone === 'light'
    ? assets?.logo_light || assets?.logo_full
    : assets?.logo_full;

  const a11y = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': 'Glanz Luxe' } as const);

  /*
   * Real artwork wins whenever it exists and is not the unshipped default.
   * The check is deliberate: config ships a placeholder path so the shape of
   * the settings payload is stable, but that file may not be present yet.
   */
  if (asset && !asset.endsWith('/brand/logo-full.svg') && !asset.endsWith('/brand/logo-light.svg')) {
    return (
      <img
        src={asset}
        alt={decorative ? '' : 'Glanz Luxe'}
        width={PIXEL_WIDTHS[size]}
        className={`h-auto ${className}`}
        {...(decorative ? { 'aria-hidden': true } : {})}
      />
    );
  }

  const scale = SIZES[size];
  const luxeColor = tone === 'dark' ? 'text-charcoal' : 'text-white';

  return (
    <span className={`inline-flex flex-col items-center ${className}`} {...a11y}>
      {/* Gold script wordmark. Extra bottom padding because the descender on
          the "z" swings well below the baseline in the real lettering. */}
      <span className={`font-script leading-[1.1] pb-2 text-accent ${scale.glanz}`}>Glanz</span>

      <span className={`font-sans font-normal uppercase leading-none ${luxeColor} ${scale.luxe} ${scale.gap}`}>
        Luxe
      </span>

      {/* Gold rule with the heart, broken either side — sits BELOW "LUXE". */}
      <span className={`mt-2.5 flex items-center justify-center gap-2 ${scale.rule}`} aria-hidden="true">
        <span className="h-px flex-1 bg-accent" />
        <svg viewBox="0 0 24 24" className={`shrink-0 fill-accent ${scale.heart}`} focusable="false">
          <path d="M12 21.6 3.9 13.2A5.4 5.4 0 0 1 11.6 5.6l.4.4.4-.4a5.4 5.4 0 0 1 7.7 7.6Z" />
        </svg>
        <span className="h-px flex-1 bg-accent" />
      </span>
    </span>
  );
}
